<template>
  <div>
    <div class="mb-3">
      <router-link :to="backRoute" class="btn btn-outline-primary btn-sm">
        <i class="fa fa-arrow-left"></i> Voltar
      </router-link>
    </div>

    <div v-if="state === 'loading'" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
    </div>

    <div v-else-if="state === 'error'" class="alert alert-danger">
      Não foi possível carregar os dados da consulta. Tente novamente mais tarde.
    </div>

    <div v-else-if="state === 'not_allowed'" class="alert alert-warning">
      Esta consulta não está mais disponível para agendamento de retorno.
    </div>

    <template v-else-if="schedule != null">
      <div class="card shadow rounded border-0 mb-4">
        <div class="card-body">
          <h5 class="card-title mb-3">Agendar retorno</h5>
          <div class="row">
            <div class="col-md-2 text-center text-md-start mb-3 mb-md-0">
              <img
                v-if="schedule.prof_photo"
                :src="url(schedule.prof_photo)"
                class="avatar avatar-md-md shadow-md rounded-circle"
                alt="Foto do profissional"
              >
              <img
                v-else
                src="../../assets/images/no-photo.png"
                class="avatar avatar-md-md shadow-md rounded-circle"
                alt="Foto do profissional"
              >
            </div>
            <div class="col-md-5 text-start">
              <p class="text-dark mb-2">
                {{ schedule.prof_name }} <br>
                <span class="badge bg-primary">{{ schedule.spec_name }}</span>
              </p>
              <p class="text-dark mb-0">
                <b>Local:</b> {{ schedule.place_name }} <br>
                <b>Endereço:</b> {{ schedule.place_address || schedule.address }} <br>
                <b>Paciente:</b> {{ schedule.patient_name }}
                <span v-if="isOnlineSchedule" class="badge bg-primary ms-1">Online</span>
              </p>
            </div>
            <div class="col-md-5 text-start">
              <p class="text-dark mb-0">
                <b>Consulta anterior:</b> <br>
                {{ date(schedule.sched_datetime_patient).format('DD/MM/YYYY HH:mm') }} <br>
                <b>Forma de pagamento:</b> {{ schedule.paymeth_name }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="card border-0 rounded shadow">
        <div class="card-body">
          <h5 class="card-title">Escolha um dia e horário</h5>
          <p class="text-muted">Horários liberados exclusivamente para retorno.</p>
          <hr>
          <Times
            v-if="professional != null"
            :id_prof="schedule.id_prof"
            :id_place="schedule.id_place"
            :id_spec="schedule.id_spec"
            :id_city="isOnlineSchedule ? null : schedule.id_city"
            :id_insurance="schedule.id_insurance"
            :id_paymeth="schedule.id_paymeth"
            :id_sched_father="schedule.id_sched"
            :is-return="true"
            :professional="professional"
            @onClickDate="scheduleReturn"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Times from '../components/Times.vue'
import http from '@/helpers/http'
import Alert from '@/helpers/alert'
import Swal from 'sweetalert2'
import date from '@/helpers/date'
import str from '@/helpers/str'
import Device from '@/helpers/device'

export default {
  name: 'ScheduleReturn',
  components: {
    Times,
  },
  data() {
    return {
      state: 'loading',
      schedule: null,
      professional: null,
      startEndDays: Device.isMobile() ? 3 : 7,
      submitting: false,
    }
  },
  computed: {
    userAuth() {
      return this.$store.state.auth.user
    },
    scheduleId() {
      return this.$route.params.scheduleId
    },
    backRoute() {
      return this.$route.query.from === 'scheduling' ? '/schedule/patients' : '/history'
    },
    isOnlineSchedule() {
      return !!(this.schedule?.sched_online || this.schedule?.avail_online)
    },
  },
  mounted() {
    this.loadSchedule()
  },
  methods: {
    date,
    url(path) {
      return str.url(path)
    },
    loadSchedule() {
      if (!this.userAuth?.id_user) {
        this.state = 'error'
        return
      }

      this.state = 'loading'

      http.get(`/users/${this.userAuth.id_user}/schedules/${this.scheduleId}`)
        .then((response) => {
          if (!response.data) {
            this.state = 'error'
            return Promise.reject()
          }

          if (!response.data.allow_return) {
            this.schedule = response.data
            this.state = 'not_allowed'
            return Promise.reject()
          }

          this.schedule = response.data
          return this.loadProfessional()
        })
        .then(() => {
          this.state = 'loaded'
        })
        .catch(() => {
          if (this.state === 'loading') {
            this.state = 'error'
          }
        })
    },
    loadProfessional() {
      const params = {
        id_place: this.schedule.id_place,
        id_city: this.isOnlineSchedule ? null : this.schedule.id_city,
        id_spec: this.schedule.id_spec,
        id_paymeth: this.schedule.id_paymeth,
        id_insurance: this.schedule.id_insurance,
        with_availability: true,
        start_end_days: this.startEndDays,
        id_prof: this.schedule.id_prof,
        is_return: 'S',
      }

      return http.get(`/schedule/professionals/${this.schedule.id_prof}`, { params })
        .then((response) => {
          this.professional = response.data
        })
    },
    scheduleReturn(availabilityTime) {
      if (this.submitting) {
        return
      }

      this.submitting = true

      const param = {
        sched_date: availabilityTime.available_date,
        sched_time: availabilityTime.available_time,
        sched_datetime_patient: availabilityTime.sched_datetime_patient,
      }

      if (availabilityTime.id_avail != null && availabilityTime.id_avail !== '') {
        param.id_avail = availabilityTime.id_avail
      }

      if (availabilityTime.id_place_location) {
        param.id_place_location = availabilityTime.id_place_location
      }

      http.post(`/users/${this.userAuth.id_user}/schedules/${this.scheduleId}/return`, param)
        .then(() => {
          Swal.fire({
            title: 'Retorno agendado!',
            text: 'Seu retorno foi agendado com sucesso.',
            icon: 'success',
            allowOutsideClick: false,
            confirmButtonText: 'Ver meus agendamentos',
            confirmButtonColor: '#780f5a',
          }).then(() => {
            this.$router.push('/schedules')
          })
        })
        .catch((e) => Alert.error(e.response?.data))
        .finally(() => {
          this.submitting = false
        })
    },
  },
}
</script>

<style scoped>
</style>
